<script lang="ts">
  import { onMount } from "svelte"
  import CircleProgressBar from "$app/components/CircleProgressBar.svelte"
  import { slide } from "svelte/transition"
  import StepIcon from "./StepIcon.svelte"
  import { xhr } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import { appStore } from "$app/store.svelte"
  import {
    CircleCheck,
    Clock,
    Lock,
    ChevronLeft,
    CircleMinus
  } from "lucide-svelte"

  interface Props {
    course_progress: CourseProgressType
    onUpdateProgress: (course_progress: CourseProgressType) => void
    purchaseCourse: () => void
  }
  let {
    course_progress = $bindable(),
    onUpdateProgress,
    purchaseCourse
  }: Props = $props()

  let expanded_step = $state(0)

  onMount(() => {
    expanded_step = course_progress.step
  })

  const openStep = (step) => {
    if (
      !course_progress.purchased &&
      step.step_num > appStore.course.max_free_step
    ) {
      purchaseCourse()
    } else {
      expanded_step = step.step_num
    }
  }

  const completedStep = (step_num) => {
    let substep_count = appStore.course.steps[step_num - 1].substeps.length
    return (
      course_progress.completed_step > step_num ||
      (course_progress.completed_step == step_num &&
        course_progress.completed_substep >= substep_count)
    )
  }

  const nextStep = (step_num) => {
    return course_progress.next_step == step_num
  }

  const completedSubstep = (step_num, substep_num) => {
    return (
      course_progress.completed_step > step_num ||
      (course_progress.completed_step == step_num &&
        course_progress.completed_substep >= substep_num)
    )
  }

  const nextSubstep = (step_num, substep_num) => {
    return (
      course_progress.next_step == step_num &&
      course_progress.next_substep === substep_num
    )
  }

  const activeSubstep = (step_num, substep_num) => {
    return (
      course_progress.completed_step > step_num ||
      (course_progress.completed_step == step_num &&
        course_progress.completed_substep >= substep_num) ||
      (course_progress.next_step == step_num &&
        course_progress.next_substep === substep_num)
    )
  }

  let updatingStep = false
  async function updateStep(step_num, substep_num): Promise<void> {
    updatingStep = true

    if (activeSubstep(step_num, substep_num) === false) {
      return
    }

    try {
      const resp = await xhr.get(
        `/plans/${appStore.plan.id}/courses/${appStore.course.slug}.json?step=${step_num}&substep=${substep_num}`
      )
      course_progress = resp.data.course_progress
      onUpdateProgress(course_progress)
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
    } finally {
      updatingStep = false
    }
  }

  const course_img = new URL(
    `/images/courses/e${appStore.course.id}_sm.jpg`,
    import.meta.url
  ).href
</script>

<div class="flex flex-col w-72 pb-4">
  {#if appStore.course}
    <div
      class="flex items-center flex-shrink-0 mb-4 bg-black rounded h-40 mr-4"
      style="background-image: url('{course_img}'); background-size: cover; background-repeat: no-repeat;"
    >
      <div
        class="relative w-full h-full bg-gray-600 opacity-80 flex items-center justify-between px-3 pt-12"
      >
        <div
          class="absolute top-4 left-4 flex items-center gap-1 text-black hover:text-white py-1 px-3 border border-white bg-blue-100 hover:bg-blue-800 rounded-lg"
        >
          <ChevronLeft class="size-4" />
          <a
            href={`/plans/${appStore.plan.id}/courses`}
            class="hover:text-white text-base">Back to Course Library</a
          >
        </div>
        <div class="text-white text-xl font-bold px-2 py-4">
          {appStore.course.name}
        </div>
        <div>
          {#key course_progress}
            <CircleProgressBar progress={course_progress.percent_progress} />
          {/key}
        </div>
      </div>
    </div>
    {#if appStore.course?.has_substeps}
      <div class="flex flex-col bg-white rounded-tl rounded-bl shadow">
        <nav class="course">
          {#each appStore.course?.steps as step}
            <button
              class:current={expanded_step === step.step_num}
              class="step"
              onclick={() => openStep(step)}
            >
              {#key course_progress}
                {#if !course_progress.purchased && step.step_num > appStore.course.max_free_step}
                  <Lock class="text-red-400 size-5 mr-1" />
                {:else if completedStep(step.step_num)}
                  <CircleCheck class="text-green-400 size-5 mr-1" />
                {:else if nextStep(step.step_num)}
                  <Clock class="text-orange-500 size-5 mr-1" />
                {:else}
                  <CircleMinus class="text-gray-300 size-5 mr-1" />
                {/if}
              {/key}
              {step.title}
            </button>

            {#if expanded_step == step.step_num}
              <div transition:slide class="bg-gray-100">
                {#key course_progress}
                  {#each step.substeps as substep}
                    <button
                      class:current={course_progress.step === step.step_num &&
                        course_progress.substep === substep.substep_num}
                      class:active={activeSubstep(
                        step.step_num,
                        substep.substep_num
                      )}
                      onclick={() =>
                        updateStep(step.step_num, substep.substep_num)}
                      class="substep"
                    >
                      <div class="flex justify-start items-center gap-2">
                        {#if completedSubstep(step.step_num, substep.substep_num)}
                          <CircleCheck class="text-green-400 size-4" />
                        {:else if nextSubstep(step.step_num, substep.substep_num)}
                          <Clock class="text-orange-500 size-4" />
                        {:else}
                          <CircleMinus class="text-gray-300 size-4" />
                        {/if}
                        {substep.title}
                      </div>
                      <div>
                        <StepIcon icon={substep.icon} />
                      </div>
                    </button>
                  {/each}
                {/key}
              </div>
            {/if}
          {/each}
        </nav>
      </div>
    {:else}
      <div class="flex flex-col bg-white rounded-tl rounded-bl shadow">
        <nav class="course">
          {#each appStore.course.steps as step}
            {#if course_progress.completed_step + 1 >= step.step_num || course_progress.completed_step === appStore.course.step_count}
              <button
                class:active={course_progress.completed_step + 1 >=
                  step.step_num}
                href="?step={step.step_num}"
                class="step hover:cursor-pointer"
                onclick={() => updateStep(step.step_num, 0)}
              >
                <div class="w-full flex justify-between items-center">
                  <div class="flex justify-start items-center gap-2">
                    {#if course_progress.next_step === step.step_num}
                      <Clock class="text-orange-500 size-5" />
                    {:else}
                      <CircleCheck class="text-green-400 size-5" />
                    {/if}
                    {step.title}
                  </div>
                  {#if step.icon}
                    <StepIcon icon={step.icon} />
                  {/if}
                </div>
              </button>
            {:else}
              <button
                class:current={course_progress.step === step.step_num}
                class="step"
                onclick={() => {
                  if (!course_progress.purchased) {
                    purchaseCourse()
                  }
                }}
              >
                <div class="w-full flex justify-between items-center">
                  <div class="flex justify-start items-center gap-2">
                    {#if !course_progress.purchased && step.step_num > appStore.course.max_free_step}
                      <Lock class="text-red-400 size-5" />
                    {:else}
                      <CircleMinus class="text-gray-300 size-5" />
                    {/if}
                    {step.title}
                  </div>
                  {#if step.icon}
                    <StepIcon icon={step.icon} />
                  {/if}
                </div>
              </button>
            {/if}
          {/each}
        </nav>
      </div>
    {/if}
  {/if}
</div>
